import { useCallback, useEffect, useMemo, useRef } from "react";
import { InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import {
  QueryKeyArray,
  UseAPIOptions,
  createAuthRetryHandler,
  // Despite the name this is a plain async request function, not a hook, and
  // calling it inside queryFn trips the rules-of-hooks name heuristic.
  useAPIHelper as requestApiEndpoint
} from "@/services/api/hook";
import { useAuth } from "@/services/auth/AuthContext";

/**
 * The paging rule every one of these endpoints answers with: pages are
 * numbered from one and the last one is `pages`.
 */
export const nextPageNumber = ({
  page,
  pages
}: {
  page: number;
  pages: number;
}) => (page < pages ? page + 1 : undefined);

interface InfiniteFilterQueryParams<TPage> {
  /** The route key the request is made against. */
  routeKey: string;
  /**
   * The request as `useAPI` would take it, memoized by the caller: it is part
   * of the query key, so a fresh object on every render would refetch forever.
   */
  requestOptions: UseAPIOptions;
  enabled?: boolean;
  /**
   * The page to ask for after this one, or undefined at the end of the list.
   * Most callers hand the response's pagination straight to `nextPageNumber`.
   */
  getNextPageParam: (lastPage: TPage) => number | undefined;
  refetchOnMount?:
    | boolean
    | ((query: { state: { isInvalidated: boolean } }) => boolean);
  staleTime?: number;
  gcTime?: number;
  /** Rendered while the first fetch is in flight, for a caller with a cache. */
  placeholderData?: () => InfiniteData<TPage, number> | undefined;
}

/**
 * An infinite query over an endpoint that takes its filter as a POST body and
 * its paging on the query string — a shape `useAPI` cannot express, since it
 * has no page to advance.
 *
 * The page number is injected into `queryParams` inside `queryFn` and the key
 * rebuilt from it, so each page is requested and cached under its own key while
 * the caller keeps one key for the whole list.
 *
 * What a caller layers on top — a debounce, a store, a disk cache — stays with
 * the caller; this owns the request, the auth it carries, and the paging.
 */
export const useInfiniteFilterQuery = <TPage,>({
  routeKey,
  requestOptions,
  enabled = true,
  getNextPageParam,
  refetchOnMount,
  staleTime,
  gcTime,
  placeholderData
}: InfiniteFilterQueryParams<TPage>) => {
  const { isAuthenticated, token, tenantId, tokenExpiry, login } = useAuth();

  // Read inside queryFn rather than captured, so a token refreshed mid-scroll
  // is the one the next page is fetched with. Written after commit rather than
  // during render: a render React discards would otherwise leave the committed
  // tree fetching under credentials it never rendered with.
  const authRef = useRef({ token, tenantId, tokenExpiry });
  useEffect(() => {
    authRef.current = { token, tenantId, tokenExpiry };
  }, [token, tenantId, tokenExpiry]);

  // The tenant scopes the request through its header, so it has to scope the
  // cache too — without it a tenant switch under a live QueryClient serves the
  // previous tenant's pages for the same route and body.
  const queryKey: QueryKeyArray = useMemo(
    () => [routeKey, requestOptions, { tenantId }],
    [routeKey, requestOptions, tenantId]
  );

  const result = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 1, signal, queryKey: currentQueryKey }) => {
      const currentOptions = currentQueryKey[1] as UseAPIOptions;
      const pageOptions: UseAPIOptions = {
        ...currentOptions,
        queryParams: {
          ...currentOptions.queryParams,
          page: String(pageParam)
        }
      };
      const pageQueryKey: QueryKeyArray = [routeKey, pageOptions];
      return (await requestApiEndpoint(
        authRef.current,
        pageQueryKey,
        signal
      )) as TPage;
    },
    getNextPageParam,
    initialPageParam: 1,
    enabled: isAuthenticated && enabled,
    retry: createAuthRetryHandler(login),
    refetchOnMount,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime,
    gcTime,
    placeholderData
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage: fetchNext } = result;

  const fetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNext();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNext]);

  return {
    queryKey,
    data: result.data,
    pages: result.data?.pages,
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
    isPlaceholderData: result.isPlaceholderData,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  };
};
